/*
 * Copyright (c) Worldline 2022.
 */

import {Component, OnInit} from '@angular/core';
import {NgxSpinnerService} from "ngx-spinner";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {Response} from "../../model/response";
import less from "less"
import {SharedService} from "../../services/shared.service";

@Component({
    selector: 'app-customization',
    templateUrl: './customization.component.html'
})
export class CustomizationComponent implements OnInit {

    private static COLORS_FILE = 'styles/includes/variables.less'

    private static SPECIFIC_FILE = 'styles/specific.less'

    // trick to loop on a map from HTML files
    Object = Object;

    colors: any = {}

    fileUrl: string | null = null

    private specific: string | null = null

    constructor(private spinner: NgxSpinnerService, private shared: SharedService, private httpClient: HttpClient) {
    }

    ngOnInit(): void {
        this.init()
    }

    /**
     * Upload a default CSS file to update with the new colors and initialize the color pickers
     * @param event the upload event containing the file
     */
    upload(event: any) {
        let file = event.files[0]
        let reader = new FileReader()
        let fileExtension = file.name.split('.')
        let that = this;

        reader.onload = function () {
            let fileContent = reader.result as string
            if (fileExtension[1] == 'css') {
                that.fileUrl = null;
                const colorRegexp = /#[0-9a-f]{6}/i;

                // parse CSS file (loops as long as there is an opening CSS comment tag)
                while ((fileContent.match(/\/\*/g) || []).length) {
                    const endComment = fileContent.indexOf('*/');
                    const commentBlock = fileContent.substring(fileContent.indexOf('/*'), endComment);
                    fileContent = fileContent.substring(endComment + 2);
                    const comments = commentBlock.split(';');
                    // parse CSS comment block to extract color name and color value
                    for (let comment of comments) {
                        if (comment.indexOf('@') > -1) {
                            const line = comment.split(':');
                            const beginIndex = line[0].indexOf('@');
                            if (beginIndex !== -1) {
                                const colorName = line[0].substring(beginIndex + 1).trim();
                                const colorValue = line[1].trim();
                                // check format of color variables
                                if (colorValue.match(colorRegexp)) {
                                    that.colors[colorName].value = colorValue;
                                } else {
                                    return;
                                }
                            }
                        }
                    }
                }
            }
        }
        reader.readAsText(file)
    }

    /**
     * Compile the specific.less file with the new colors and dynamically change the style of the application with this generated style
     */
    compile() {
        this.showSpinner();
        this.fileUrl = null;
        console.debug('[tenant-customization] start compiling');
        setTimeout(() => {
            // add colors from scope
            let newColorsComments = '/*\n';
            let newColors = '';
            for (let color of Object.keys(this.colors)) {
                const line = '@' + color + ': ' + this.colors[color].value + ';\n';
                newColors = newColors + line;
                newColorsComments = newColorsComments + '  ' + line;
            }
            newColorsComments = newColorsComments + '*\n\n';
            const newLess = newColors + this.specific;

            less.render(newLess, {}, (error, output) => {
                console.debug('[tenant-customization] less output : ', output);
                if (!error) {
                    const blob = new Blob([newColorsComments + output.css], {
                        type: 'text/plain'
                    });
                    this.fileUrl = (window.URL || window.webkitURL).createObjectURL(blob);
                    // Add '!important' for each property to override already loaded CSS
                    const importantCss = output.css.replace(/;/g, ' !important;')
                    this.shared.broadcast('add-custom-style', {
                        css: importantCss
                    });
                    this.hideSpinner()
                } else {
                    this.hideSpinner()
                    throw {
                        data: {
                            toastr: {
                                header: 'Error during tenant customization',
                                details: error.message
                            }
                        }
                    };
                }
            });
        }, 100);
    };

    /**
     * Start the downloading of the generated CSS file
     */
    download() {
        if (this.fileUrl) {
            let a = document.createElement('a');
            a.href = this.fileUrl;
            a.download = "specific.css";
            a.click();
            document.removeChild(a)
        }
    }

    /**
     * Download a CSS file and add it in the previousResult list
     * @param filename the name of the file to download
     * @param previousResult the list of all previous CSS files downloaded
     */
    private getCssFile(filename: string, previousResult) {
        const httpOptions: Object = {
            headers: new HttpHeaders({
                'Accept': '*/*',
            }),
            responseType: 'text'
        }
        return this.httpClient.get<Response>(filename, httpOptions).toPromise()
            .then((response) => {
                previousResult[filename] = response;
                return previousResult;
            });
    }

    /**
     * Download Signbox CSS files to parse default variables colors
     */
    private init() {
        this.showSpinner()
        this.getCssFile(CustomizationComponent.COLORS_FILE, {})
            .then(result => this.getCssFile(CustomizationComponent.SPECIFIC_FILE, result))
            .then(result => {
                // process colors file
                let linesColors = result[CustomizationComponent.COLORS_FILE].split('\n')

                for (let lineColor of linesColors) {
                    if (lineColor.indexOf('@') === 0) {
                        let name = lineColor.substring(1, lineColor.indexOf(':'))
                        let value = lineColor.substring(lineColor.indexOf('#'), lineColor.indexOf(';'))
                        let i18n = lineColor.substring(lineColor.indexOf('//') + 2).replace(' ', '')
                        if (name && value) {
                            this.colors[name] = {
                                value: value,
                                name: name,
                                i18n: i18n,
                            }
                        }
                    }
                }

                // process specific.less
                let linesSpecific = result[CustomizationComponent.SPECIFIC_FILE].split('\n')
                let newCssData = '';
                // remove imports (default colors)
                for (let line of linesSpecific) {
                    if (line.indexOf('@import') > -1 && line.indexOf('.less') > -1) {
                        console.debug('[tenant-customization] [specific.less] skipping following less import : ' + line);
                    } else {
                        newCssData = newCssData + line + '\n';
                    }
                }
                this.specific = newCssData;
            })
            .catch(error => {
                throw {
                    data: {
                        toastr: {
                            header: 'Error during tenant customization initialization',
                            details: error
                        }
                    }
                };
            })
            .finally(() => this.hideSpinner())
    }

    private showSpinner() {
        this.spinner.show('spinner')
            .then(() => console.trace('spinner enabled'))
    }

    private hideSpinner() {
        this.spinner.hide('spinner')
            .then(() => console.trace('spinner disabled'))
    }
}
